<!-- @prettier -->
<template>
  <ValidationObserver
    ref="form"
    v-slot="{ handleSubmit, invalid, pristine }"
    slim
  >
    <form
      role="form"
      class="form-horizontal"
      data-testid="affiliateForm"
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <fieldset>
        <!-- name -->
        <Field
          :label="$t('PUBLISHER_COMPANY')"
          :has-tooltip="true"
          :tooltip-text="$t('PUBLISHER_COMPANY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="name"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-xs-6 col-sm-4 col-md-4">
                  <input
                    v-model="currentPublisher.name"
                    :placeholder="$t('PUBLISHER_COMPANY_DEFAULT')"
                    type="text"
                    name="name"
                    autocomplete="off"
                    class="form-control"
                    custom-autofocus="true"
                    data-testid="affiliateFormName"
                  />
                </div>
                <label
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- subId -->
        <Field
          :label="$t('PUBLISHER_SUBID')"
          :has-tooltip="true"
          :tooltip-text="$t('PUBLISHER_SUBID_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="subId"
              slim
            >
              <div :class="classes">
                <div class="col-xs-6 col-sm-4 col-md-4">
                  <input
                    v-model="currentPublisher.subId"
                    type="text"
                    :placeholder="$t('PUBLISHER_SUBID_DEFAULT')"
                    name="subId"
                    autocomplete="off"
                    class="form-control"
                    data-testid="affiliateFormSubId"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- Number Creation -->
        <Field
          :label="$t('PUBLISHER_NUMBER_CREATION')"
          :has-tooltip="true"
          :tooltip-text="$t('PUBLISHER_NUMBER_CREATION_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="createNumbers"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-md-8 col-sm-8">
                  <NzToggle
                    v-model="currentPublisher.createNumbers"
                    name="createNumbers"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- Block calls on cap -->
        <Field
          :label="$t('ACCOUNT_SETTINGS_PUBLISHER_BLOCK_CALLS_ON_CAP')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('PUBLISHER_ACCEPT_CALLS_ON_CAP_TT')"
          class="m-b-5"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="settingToggleBlockCallsIfCapped"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="ol-sm-9 col-md-9">
                  <MultipleToggleOverride
                    toggle-name="settingToggleBlockCallsIfCapped"
                    :value="currentPublisher.blockCallsIfCapped"
                    :options="blockCallsIfCappedOptions"
                    :disabled="false"
                    @onToggle="toggleBlockCallsOnPubCapValueChanged"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- PUBLISHER_ACCESS_TO_RECORDINGS -->
        <Field
          :label="$t('PUBLISHER_ACCESS_TO_RECORDINGS')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('PUBLISHER_ACCESS_TO_RECORDINGS_TT')"
          class="m-b-5"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="rpcSetting"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="ol-sm-9 col-md-9">
                  <MultipleToggleOverride
                    toggle-name="settingToggleAccessToRecordings"
                    :value="currentPublisher.accessToRecordings"
                    :options="publisherAccessToRecordingOptions"
                    :disabled="false"
                    @onToggle="toggleBlockAccessToRecordingsValueChanged"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <div
          class="form-group"
          data-testid="submit-wrapper"
        >
          <div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">
            <p
              v-if="invalid && triedToSend"
              class="m-t-8 m-b-8 bright-red"
            >
              <i class="f-s-20 p-r-0 typcn typcn-warning" />{{
                $t('INVALID_FORM')
              }}
            </p>
            <PrimaryButton
              size="sm"
              :show-loader-on-form="showLoader"
              data-testid="submit"
              @click="triedToSend = true"
            >
              <span v-t="currentPublisher.id ? 'UPDATE' : 'CREATE'" />
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              class="btn btn-sm btn-danger m-l-15"
              data-testid="cancel"
              @click="cancel"
            />
          </div>
        </div>
      </fieldset>
    </form>
  </ValidationObserver>
</template>

<script>
import Field from '@/core/components/LabelAndElement/LabelAndElement.vue';
import PrimaryButton from '@/core/components/PrimaryButton';
import NzToggle from '@/core/components/nzToggle/nzToggle';
import MultipleToggleOverride from '@/core/components/MultipleToggleOverride/MultipleToggleOverride';
import messages from '../locales';
import sharedMessages from '@/core/locales';

export default {
  name: 'PublisherForm',
  components: {
    Field,
    PrimaryButton,
    NzToggle,
    MultipleToggleOverride
  },
  props: {
    publisher: {
      type: Object,
      default: () => ({})
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    accountSettings:{
      type: Object,
      default: () => ({})
    },
  },
  data: () => ({
    currentPublisher: {
      createNumbers: false,
      blockCallsIfCapped: null,
      accessToRecordings: null
    },
    triedToSend: false,
  }),
  computed: {
    blockCallsIfCappedOptions() {
      const blockIfCapped = this.accountSettings?.blockCallsIfCapped;
      return [
        {
          name: this.$t('ACCOUNT_SETTING_TOGGLE_GLOBAL'),
          value: null,
          state: {
            text: this.$t(
              blockIfCapped == true ? `ACCOUNT_SETTING_TOGGLE_PUBLISHER_BLOCK_CALLS_ON_CAP_SETTING_DISABLED`
                : 'ACCOUNT_SETTING_TOGGLE_PUBLISHER_BLOCK_CALLS_ON_CAP_SETTING_ENABLED'
            ),
            link: '/#/dashboard/user-account-settings'
          }
        },
        {
          name: this.$t(
            'ACCOUNT_SETTING_TOGGLE_PUBLISHER_BLOCK_CALLS_ON_CAP_SETTING_ENABLED'
          ),
          value: false
        },
        {
          name: this.$t(
            'ACCOUNT_SETTING_TOGGLE_PUBLISHER_BLOCK_CALLS_ON_CAP_SETTING_DISABLED'
          ),
          value: true
        }
      ];
    },
    publisherAccessToRecordingOptions() {
			const pubAccess = this.accountSettings?.publisherAccessToRecording;
      return [
        {
          name: this.$t('ACCOUNT_SETTING_TOGGLE_GLOBAL'),
          state: {
            text: this.$t(
              pubAccess == true ? 'ACCOUNT_SETTING_TOGGLE_PUBLISHER_RECORDING_SETTING_ENABLED'
                : 'ACCOUNT_SETTING_TOGGLE_PUBLISHER_RECORDING_SETTING_DISABLED'
            ),
            link: '/#/dashboard/user-account-settings'
          },
          value: null
        },
        {
          name: this.$t(
            'ACCOUNT_SETTING_TOGGLE_PUBLISHER_RECORDING_SETTING_ENABLED'
          ),
          value: true
        },
        {
          name: this.$t(
            'ACCOUNT_SETTING_TOGGLE_PUBLISHER_RECORDING_SETTING_DISABLED'
          ),
          value: false
        }
      ];
    }
  },
  async created() {
    if (this.publisher && this.publisher.id) {
      this.currentPublisher = _.cloneDeep(this.publisher);
    }
  },
  methods: {
    toggleBlockCallsOnPubCapValueChanged(blockCallsIfCapped) {
      this.currentPublisher.blockCallsIfCapped = blockCallsIfCapped.value;
    },
    toggleBlockAccessToRecordingsValueChanged(accessToRecordings) {
      this.currentPublisher.accessToRecordings = accessToRecordings.value;
    },
    onSubmit() {
      this.triedToSend = true;

      // Trim whitespaces from the name field before save
      if (this.currentPublisher.name) {
        this.currentPublisher.name = this.currentPublisher.name.trim();
      }

      const payload = { publisher: this.currentPublisher };
      if (!this.currentPublisher.id) {
        payload.publisher.doNotCreateUser = true;
      }
      this.$emit('submit', payload);
    },
    cancel(evt) {
      evt.preventDefault();
      this.$emit('cancel', this.currentPublisher.id ? this.publisher : null);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
